import React, { useState } from 'react';
import {
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Alert,
  Box,
  CircularProgress,
  Tooltip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Flag as FlagIcon,
  Send as SendIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';

const REPORT_CATEGORIES = [
  { value: 'complaint', label: 'Complaint' },
  { value: 'request', label: 'Request' },
  { value: 'bug', label: 'Bug Report' },
  { value: 'abuse', label: 'Abuse / Harassment' },
  { value: 'spam', label: 'Spam' },
  { value: 'misinformation', label: 'Misinformation' },
  { value: 'copyright', label: 'Copyright Violation' },
  { value: 'privacy', label: 'Privacy Concern' },
  { value: 'other', label: 'Other' },
];



/**
 * Reusable Report button + dialog.
 *
 * Props:
 *  - targetType   : string  — 'user' | 'organization' | 'consortium' | 'fundingcall' | 'post' | 'reflection'
 *  - targetId     : string  — UUID public_id of the target entity
 *  - targetLabel  : string  — optional human name shown in the dialog title
 *  - variant      : 'icon' | 'button' | 'menu'  (default 'icon')
 *  - size         : 'small' | 'medium'           (default 'small')
 *  - color        : string                        (default 'default')
 *  - sx           : object                        (extra styles on the trigger)
 */
const ReportButton = ({
  targetType,
  targetId,
  targetLabel,
  variant = 'icon',
  size = 'small',
  color = 'default',
  sx = {},
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleOpen = (e) => {
    e?.stopPropagation?.();
    setOpen(true);
    setForm({ category: '', subject: '', message: '' });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    if (!submitting) setOpen(false);
  };

  const handleSubmit = async () => {
    setError(null);
    if (!form.category || !form.subject || !form.message) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    try {
      await axiosService.post('/interact/reports/', {
        ...form,
        target_type: targetType,
        target_id: targetId,
      });
      setSuccess(true);
      setTimeout(() => setOpen(false), 1800);
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        typeof data === 'string'
          ? data
          : data?.detail || data?.message || 'Failed to submit report.';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Trigger element ──
  const trigger =
    variant === 'button' ? (
      <Tooltip title="Report">
        <Button
          size={size}
          color={color}
          variant="outlined"
          startIcon={<FlagIcon />}
          onClick={handleOpen}
          sx={sx}
        >
          Report
        </Button>
      </Tooltip>
    ) : (
      <Tooltip title="Report">
        <IconButton size={size} color={color} onClick={handleOpen} sx={sx} aria-label="Report">
          <FlagIcon fontSize={size} />
        </IconButton>
      </Tooltip>
    );

  return (
    <>
      {trigger}

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              display: 'flex',
            }}
          >
            <FlagIcon fontSize="small" />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="span">
              Report{targetLabel ? `: ${targetLabel}` : ''}
            </Typography>
            {targetType && (
              <Typography variant="caption" color="text.secondary" display="block">
                Reporting {targetType}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={submitting} aria-label="Close report dialog">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {success ? (
            <Alert severity="success" sx={{ borderRadius: 1 }}>
              Report submitted — thank you! Our team will review it shortly.
            </Alert>
          ) : (
            <Stack spacing={2.5} sx={{ pt: 0.5 }}>
              {error && (
                <Alert severity="error" sx={{ borderRadius: 1 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={form.category}
                  onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
                  label="Category"
                >
                  {REPORT_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Subject"
                value={form.subject}
                onChange={(e) => setForm((p) => ({ ...p, subject: e.target.value }))}
                required
                placeholder="Brief summary"
              />

              <TextField
                fullWidth
                label="Describe the issue"
                value={form.message}
                onChange={(e) => setForm((p) => ({ ...p, message: e.target.value }))}
                required
                multiline
                rows={4}
                placeholder="Please provide as much detail as possible…"
              />
            </Stack>
          )}
        </DialogContent>

        {!success && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleSubmit}
              disabled={submitting}
              startIcon={
                submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />
              }
            >
              {submitting ? 'Submitting…' : 'Submit Report'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export default ReportButton;
